import React from 'react';
import { Tooltip, Tag, Space } from 'antd';
import { VideoCameraOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { ExtendedEvent } from './types';
import { REGULAR_EVENT_TYPES, RECURRENCE_OPTIONS } from './constants';
import { toLocalDate } from '../../helpers/dateConverters';

const WEEKDAY = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

function formatRecurrence(rule: ExtendedEvent['recurrence']): string | null {
  // Return null if no rule exists
  if (!rule) return null;

  // Handle numeric patterns (0 typically means no recurrence)
  if (typeof rule.pattern === 'number') {
    if (rule.pattern === 0) return null;
    // Map numeric values to string patterns if needed
    const patternMap: Record<number, string> = {
      1: 'daily',
      2: 'weekly',
      3: 'monthly',
      4: 'yearly'
    };
    const mappedPattern = patternMap[rule.pattern];
    if (!mappedPattern) return null;

    // Find the option using the mapped pattern
    const option = RECURRENCE_OPTIONS.find(r => r.value === mappedPattern);
    if (!option) return null;

    const interval = rule.interval && rule.interval > 0 ? rule.interval : 1;
    return formatRecurrenceString(option.label, interval, rule, mappedPattern);
  }

  // Handle string patterns
  if (typeof rule.pattern === 'string') {
    // Find the recurrence option
    const option = RECURRENCE_OPTIONS.find(r => r.value === rule.pattern);
    if (!option) return null;

    const interval = rule.interval && rule.interval > 0 ? rule.interval : 1;
    return formatRecurrenceString(option.label, interval, rule, rule.pattern);
  }

  return null;
}

function formatRecurrenceString(
  label: string,
  interval: number,
  rule: ExtendedEvent['recurrence'],
  pattern: string
): string {
  let recurString: string;

  if (interval === 1) {
    // For interval of 1, just show "Daily", "Weekly", etc.
    recurString = label;
  } else {
    // For interval > 1, show "Every 2 weeks", "Every 3 days", etc.
    const unitLabel = label.toLowerCase();
    // Handle pluralization
    const unit = interval > 1 && !unitLabel.endsWith('ly')
      ? unitLabel + 's'
      : unitLabel.replace('ly', 's');
    recurString = `Every ${interval} ${unit}`;
  }

  // Add weekday info for weekly recurrence
  if (pattern === 'weekly' && rule && Array.isArray(rule.byWeekday) && rule.byWeekday.length) {
    const days = rule.byWeekday.map(d => WEEKDAY[d] ?? d).join(', ');
    recurString += ` (${days})`;
  }

  return recurString;
}

interface Props {
  event: ExtendedEvent;
  t: (k: string) => string;
}

const EventComponent: React.FC<Props> = ({ event, t }) => {
  const cfg = REGULAR_EVENT_TYPES.find(c => c.type === event.eventType);
  const icon = event.type === 'project' ? '📋' : event.type === 'crm' ? '🤝' : cfg?.icon ?? '📅';
  const label = event.title;
  const recurInfo = formatRecurrence(event.recurrence);
  const recurIcon = recurInfo ? '🔄 ' : '';
  const onlineMeetingIcon = event.isOnlineMeeting ? <VideoCameraOutlined style={{ marginRight: 4 }} /> : null;

  // Extract time from the actual event start/end dates if startTime/endTime are missing
  const getLocalTime = (date?: Date | string) => {
    if (!date) return ''
    return dayjs(toLocalDate(date)).format('HH:mm')
  }

  const startTime =
    event.startTime ||
    (event.start
      ? event.type === 'project'
        ? getLocalTime(event.start)
        : dayjs(event.start).format('HH:mm')
      : '')

  const endTime =
    event.endTime ||
    (event.end
      ? event.type === 'project'
        ? getLocalTime(event.end)
        : dayjs(event.end).format('HH:mm')
      : '')

  const hasTime = startTime && endTime

  return (
    <Tooltip
      title={
        <div>
          <strong>{label}</strong>
          {event.userName && (
            <>
              <br />
              <span>{event.userName}</span>
            </>
          )}
          {event.type === 'project' && (
            <>
              {hasTime && (
                <>
                  <br />
                  {t("Time")}: {startTime} – {endTime}
                </>
              )}
              {event.participants !== undefined && (
                <>
                  <br />
                  {t("Participants")}: {Array.isArray(event.participants) ? event.participants.length : 0}
                </>
              )}
              {event.needsApproval && (
                <>
                  <br />
                  {t("Status")}:{' '}
                  <Tag color={event.status === 'pending' ? 'orange' : 'green'}>
                    {event.status}
                  </Tag>
                </>
              )}
              {recurInfo && (
                <>
                  <br />
                  {t("Recurrence")}: {recurInfo}
                </>
              )}
              {event.isOnlineMeeting && (
                <>
                  <br />
                  <Space>
                    <VideoCameraOutlined />
                    {t("OnlineMeeting")}
                  </Space>
                </>
              )}
              {event.description && (
                <>
                  <br />
                  <br />
                  {event.description}
                </>
              )}
            </>
          )}
        </div>
      }
    >
      <span className='calendar-events' style={{ fontSize: 11, display: 'flex', alignItems: 'center' }}>
        {recurIcon}
        {onlineMeetingIcon}
        {icon} {label}
      </span>
    </Tooltip>
  );
};

export default EventComponent;